import { useTranslation } from "react-i18next";
import type { CollectionStepCompletion } from "@/hooks/useCollections";

interface StepCardInfo {
  id: string;
  title: string | null;
  body: string;
  card_type: string;
  capture_type: string | null;
  instructions: string | null;
  tip: string | null;
  tags: string[];
  voice_required: boolean;
}

export interface StepRowStep {
  id: string;
  required: boolean;
  step_title_override: string | null;
  content_library?: StepCardInfo | null;
}

interface StepRowProps {
  step: StepRowStep;
  completed: boolean;
  completion?: CollectionStepCompletion | null;
  onCapture: () => void;
  onViewMemory?: (memoryId: string) => void;
  collectionColor?: string;
  /** true when the parent collection is already completed — hides capture buttons */
  disabled?: boolean;
}

export default function StepRow({
  step,
  completed,
  completion,
  onCapture,
  onViewMemory,
  collectionColor = "#D85A30",
  disabled = false,
}: StepRowProps) {
  const { t } = useTranslation();

  const title =
    step.step_title_override ??
    step.content_library?.title ??
    "–";

  return (
    <div
      className={`flex items-start gap-3 rounded-xl px-3.5 py-3 border transition-colors ${
        completed
          ? "border-green-200/60 bg-green-50/40 dark:border-green-900/40 dark:bg-green-950/20"
          : "border-border/60 bg-card"
      }`}
    >
      {/* Status icon */}
      <div className="mt-0.5 shrink-0 text-base leading-none select-none">
        {completed ? "✅" : step.required ? "⬜" : "○"}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-[14px] font-medium leading-tight ${
            completed
              ? "text-foreground/60 line-through"
              : "text-foreground"
          }`}
        >
          {title}
        </p>

        {completed && completion ? (
          <div className="mt-1 space-y-0.5">
            <p className="text-[11px] text-muted-foreground">
              {new Date(completion.completed_at).toLocaleDateString(undefined, {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
            {onViewMemory && (
              <button
                type="button"
                onClick={() => onViewMemory(completion.memory_id)}
                className="text-[12px] font-medium underline-offset-2 hover:underline"
                style={{ color: collectionColor }}
              >
                {t("collections.step.view")}
              </button>
            )}
          </div>
        ) : (
          <div className="mt-0.5">
            {!step.required && (
              <p className="text-[11px] text-muted-foreground mb-0.5">
                {t("collections.step.optional")}
              </p>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={onCapture}
                className="text-[12px] font-medium underline-offset-2 hover:underline"
                style={{ color: collectionColor }}
              >
                {t("collections.step.capture")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
